import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { CalAm, getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SharedForm from "@widgets/SharedForm";
import SearchPanel from "@widgets/SearchPanel";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnNavBtn from "@shared/components/SnNavBtn";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_PMS_PJT_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const SharedFormHref = useRef<any>(null);
  const [focusedRowDataH, setFocusedRowDataH] = useState<any>();
  const { userInfo } = global(); // Login user 정보

  const scrollRefs = useRef<{ [key: number]: HTMLSpanElement | null }>({});
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [sharedFormH, setSharedFormH] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setSharedFormH(rtn.form.freeform1);

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    const modifiedRowsD = getGridCurrent("grid3").getChange();
    const modifiedRowsB = getGridCurrent("grid4").getChange();
    const modifiedRowsA = getGridCurrent("grid5").getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0 ||
      modifiedRowsB.modifiedRowcount > 0 ||
      modifiedRowsA.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_pms_pjt_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
        grid5: data[4] || [],
      });

      setFocusedRowDataH(null);
    } catch (error: any) {}
  };
  //#endregion

  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid5").deleteAllRows();
    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();
      return;
    }

    return await onSave();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      getGridCurrent("grid3").stopEditing();
      getGridCurrent("grid4").stopEditing();
      getGridCurrent("grid5").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L1 필수값 체크
      const requiredValuesCheckL1 =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL1) {
        return false;
      }

      // L2 필수값 체크
      const requiredValuesCheckL2 =
        getGridCurrent("grid3").requiredValuesCheck();
      if (!requiredValuesCheckL2) {
        return false;
      }

      // L3 필수값 체크
      const requiredValuesCheckL3 =
        getGridCurrent("grid4").requiredValuesCheck();
      if (!requiredValuesCheckL3) {
        return false;
      }

      // L4 필수값 체크
      const requiredValuesCheckL4 =
        getGridCurrent("grid5").requiredValuesCheck();
      if (!requiredValuesCheckL4) {
        return false;
      }

      const pjthChanges = getGridCurrent("grid1").getChange();
      // 원가 프로젝트(cd_pjt_cost) 미입력 시 주프로젝트(cd_pjt)로 자동 채운다.
      [
        ...(pjthChanges.insert ?? []),
        ...(pjthChanges.update ?? []),
      ].forEach((row: any) => {
        if (!row.cd_pjt_cost) row.cd_pjt_cost = row.cd_pjt;
      });
      const pjt_unitChanges = getGridCurrent("grid2").getChange();
      const pjt_bomChanges = getGridCurrent("grid3").getChange();
      const pjt_billChanges = getGridCurrent("grid4").getChange();
      const pjt_rcplanChanges = getGridCurrent("grid5").getChange();

      if (
        pjthChanges.modifiedRowcount === 0 &&
        pjt_unitChanges.modifiedRowcount === 0 &&
        pjt_bomChanges.modifiedRowcount === 0 &&
        pjt_billChanges.modifiedRowcount === 0 &&
        pjt_rcplanChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        pjth: pjthChanges,
        pjt_unit: pjt_unitChanges,
        pjt_bom: pjt_bomChanges,
        pjt_bill: pjt_billChanges,
        pjt_rcplan: pjt_rcplanChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_PJT_REG_001_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const visibleIdx = focusedRowNodeH.rowIndex || 0;
      getGridCurrent("grid1").clearFocus();
      getGridCurrent("grid1").selectAndFocusRow(visibleIdx);

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_gp: userInfo.cd_gp,
        nm_gp: userInfo.nm_gp,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        cd_dept: userInfo.cd_dept,
        nm_dept: userInfo.nm_dept,
        dt_contract: today,
        dt_start: today,
        dt_end: today,
        fg_pjt: "top1",
        tp_pjt: "top1",
        rt_exch: 1,
        fg_vat: "top1",
        cd_so: "top1",
        am_matl: 0,
        am_vat: 0,
        am_k: 0,
        am_labor: 0,
        am_expense: 0,
        cd_exch: "top1",
        cd_pjt: "",
        st_pjt: "F",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      SharedFormHref.current?.applyPopupConditions({
        cd_pjt_master: { st_pjt: "", cd_gp: "" },
      });
      setFocusedRowDataH(selectedRow);
    } else {
      setFocusedRowDataH(null);
    }
  };
  //#endregion

  //#region onValueChagedSharedFormH
  const onValueChagedSharedFormH = async (
    data: any,
    param: string,
    formData: any
  ) => {
    switch (param) {
      case "cd_pjt":
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        const visibleIdx = focusedRowNodeH.rowIndex || 0;
        getGridCurrent("grid1").clearFocus();
        getGridCurrent("grid1").selectAndFocusRow(visibleIdx);
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            node.setDataValue(param, data, "noEvent");
          }
        });
        getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            node.setDataValue(param, data, "noEvent");
          }
        });
        getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            node.setDataValue(param, data, "noEvent");
          }
        });
        getGridCurrent("grid5").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            node.setDataValue(param, data, "noEvent");
          }
        });
        break;
      // 26.08.06 마감일 입력시 상태갑 변경
      case "dt_exp":
        if (converter.getString(data) === "") {
          return { st_pjt: "F" };
        } else {
          return { st_pjt: "C" };
        }
    }
  };
  //#endregion

  //#region GridL1 Events
  const onBeforeAddRowL1 = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_pjt: focusedRowNodeH.data?.cd_pjt,
        no_line: maxNoLine + 1,
        dt_dlv: today,
        qt: 0,
        um: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL1 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;

    switch (colId) {
      case "qt":
      case "um":
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
    }
  };

  const onGridL1BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid2").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid2").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  //#region GridL2 Events
  const onBeforeAddRowL2 = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_pjt: focusedRowNodeH.data?.cd_pjt,
        no_line: maxNoLine + 1,
        dt_dlv: today,
        dt_plan_isu: today,
        qt: 0,
        um: 0,
        am: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL2 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;

    switch (colId) {
      case "qt":
      case "um":
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid3"], colId);
    }
  };

  const onGridL2BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid3").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid3").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid3").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  //#region GridL3 Events
  const onBeforeAddRowL3 = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid4").getMaxValue("no_line");
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_pjt: focusedRowNodeH.data?.cd_pjt,
        no_line: maxNoLine + 1,
        fg_bill: "top1",
        dt_bill_plan: today,
        rt_bill: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridL3BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid4").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid4").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  //#region GridL4 Events
  const onBeforeAddRowL4 = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid5").getMaxValue("no_line");
      const today = dateToString(new Date());

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_pjt: focusedRowNodeH.data?.cd_pjt,
        no_line: maxNoLine + 1,
        dt_start: today,
        dt_end: today,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridL4BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid5").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid5").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid5").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const SnNavBtnClick = (index: any) => {
    const targetRef = scrollRefs.current[index];
    if (targetRef) {
      targetRef.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL1,
          onCellValueChanged: onCellValueChangedL1,
          onBtnAddClick: onGridL1BtnsClick,
          onBtnDeleteClick: onGridL1BtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBeforeAddRow: onBeforeAddRowL2,
          onCellValueChanged: onCellValueChangedL2,
          onBtnAddClick: onGridL2BtnsClick,
          onBtnDeleteClick: onGridL2BtnsClick,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBeforeAddRow: onBeforeAddRowL3,
          onBtnAddClick: onGridL3BtnsClick,
          onBtnDeleteClick: onGridL3BtnsClick,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
        events: {
          onBeforeAddRow: onBeforeAddRowL4,
          onBtnAddClick: onGridL4BtnsClick,
          onBtnDeleteClick: onGridL4BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="34.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "프로젝트" }}
            events={grids.grid1.events}
            filter={{
              refs: [
                gridRefs.current["grid2"],
                gridRefs.current["grid3"],
                gridRefs.current["grid4"],
                gridRefs.current["grid5"],
              ],
              columns: ["cd_pjt"],
            }}
          />
        </SnResizeBar>

        <SnResizeBar direction={"width"} id="panel2" orgSize="65.5%">
          <div className="flex flex-col h-full">
            <div className="flex justify-start flex-none p-1">
              <SnNavBtn text="메인정보" onClick={() => SnNavBtnClick(0)} />
              <SnNavBtn text="UNIT정보" onClick={() => SnNavBtnClick(1)} />
              <SnNavBtn text="BOM정보" onClick={() => SnNavBtnClick(2)} />
              <SnNavBtn text="발행정보" onClick={() => SnNavBtnClick(3)} />
              <SnNavBtn text="대일정" onClick={() => SnNavBtnClick(4)} />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 p-1">
              <SnResizeBar direction={"height"} id="panel3" orgSize="40%">
                <span
                  ref={(el) => (scrollRefs.current[0] = el)}
                  className="block h-0 w-0"
                />
                <SharedForm
                  ref={SharedFormHref}
                  data={focusedRowDataH}
                  controls={sharedFormH}
                  gridref={gridRefs.current["grid1"]}
                  onValueChaged={onValueChagedSharedFormH}
                />
              </SnResizeBar>
              <span
                ref={(el) => (scrollRefs.current[1] = el)}
                className="block h-0 w-0"
              />
              <SnResizeBar direction={"height"} id="panel4" orgSize="40%">
                <SnGrid
                  ref={gridRefs.current["grid2"]} // Grid Name
                  columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                  rowData={grids.grid2.dataSource} // Datasource
                  etcProps={{ rowSelection: "multiple", title: "UNIT정보" }}
                  events={grids.grid2.events}
                />
              </SnResizeBar>
              <span
                ref={(el) => (scrollRefs.current[2] = el)}
                className="block h-0 w-0"
              />
              <SnResizeBar direction={"height"} id="panel5" orgSize="40%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={grids.grid3.dataSource} // Datasource
                  etcProps={{ rowSelection: "multiple", title: "BOM정보" }}
                  events={grids.grid3.events}
                />
              </SnResizeBar>
              <span
                ref={(el) => (scrollRefs.current[3] = el)}
                className="block h-0 w-0"
              />
              <SnResizeBar direction={"height"} id="panel6" orgSize="40%">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "multiple", title: "발행정보" }}
                  events={grids.grid4.events}
                />
              </SnResizeBar>
              <span
                ref={(el) => (scrollRefs.current[4] = el)}
                className="block h-0 w-0"
              />
              <SnResizeBar direction={"height"} id="panel7" orgSize="40%">
                <SnGrid
                  ref={gridRefs.current["grid5"]} // Grid Name
                  columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                  rowData={grids.grid5.dataSource} // Datasource
                  etcProps={{ rowSelection: "multiple", title: "대일정" }}
                  events={grids.grid5.events}
                />
              </SnResizeBar>
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PMS_PJT_REG_001;
